import { Button, Empty, Icon, Id, Loading } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FieldArray, useField, useFormikContext } from "formik";
import { TFunction } from "i18next";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { hotelSectionService } from "../../../shared/api/hotelSectionService";
import {
	HotelSectionFormValues,
	HotelStayConfigurationRequest,
	HotelStayFormValues,
	HotelStayStatus,
	StayConfigurationResource,
	WayOfBooking,
} from "../../../shared/types/hotel";
import { HotelSectionValidationResult, useRegistrationForm } from "../../provider/registrationFormProviderContext";
import { HotelStay } from "./HotelStay";
import { HotelStayList } from "./HotestayList";
import { RegistrationHotelSectionTitle } from "./RegistrationHotelSectionTitle";

interface Props {
	registrationFormId: Id;
	guestId: Id | null;
	sectionId: number;
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(6)};
	padding: ${({ theme }) => theme.spacing(6)} 0;
`;

const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
`;

const emptyHotelStay: HotelStayFormValues = {
	id: null,
	status: HotelStayStatus.INQUIRY,
	checkIn: "",
	checkOut: "",
	requestedHotelId: null,
	requestedRoomTypeId: null,
	remarkByGuest: null,
	cancellationRequested: false,
	additionalPersonsText: null,
	additionalPersonsEnabled: false,
	mainStay: null,
};

const getHotelStayHeading = (stayConfiguration: StayConfigurationResource, i: number, t: TFunction) => {
	if (stayConfiguration.maxStayPeriods === 1) {
		return stayConfiguration.wayOfBooking === WayOfBooking.BOOKING ? t("Booking") : t("Inquiry");
	}
	return stayConfiguration.wayOfBooking === WayOfBooking.BOOKING
		? t("{{number}}. Booking", { number: i + 1 })
		: t("{{number}}. Inquiry", { number: i + 1 });
};

const createEmptyHotelStay = (stayConfiguration: StayConfigurationResource) => {
	if (!stayConfiguration.prefillCheckInCheckOutInFrontend) {
		return emptyHotelStay;
	}
	return {
		...emptyHotelStay,
		checkIn: stayConfiguration.earliestCheckIn,
		checkOut: stayConfiguration.latestCheckOut,
	};
};

export const RegistrationHotelSection: FC<Props> = ({ registrationFormId, guestId, sectionId }) => {
	const request: HotelStayConfigurationRequest = { guestId: guestId };
	const { data: stayConfiguration, isLoading } = useQuery({
		queryKey: hotelSectionService.stayConfigurationKey(sectionId, request),
		queryFn: () => hotelSectionService.getStayConfiguration(sectionId, request),
	});

	if (isLoading) {
		return (
			<Container>
				<Loading />
			</Container>
		);
	}

	if (!stayConfiguration) {
		return null;
	}

	return (
		<RegistrationHotelSectionContent
			registrationFormId={registrationFormId}
			guestId={guestId}
			sectionId={sectionId}
			stayConfiguration={stayConfiguration}
		/>
	);
};

interface ContentProps {
	registrationFormId: Id;
	guestId: Id | null;
	sectionId: number;
	stayConfiguration: StayConfigurationResource;
}

const RegistrationHotelSectionContent: FC<ContentProps> = ({
	registrationFormId,
	guestId,
	sectionId,
	stayConfiguration,
}) => {
	const { t } = useTranslation();
	const { submitCount, setFieldTouched } = useFormikContext<Record<number, any>>();
	const { getStayConfigurationValidation } = useRegistrationForm();
	const [hotelSectionField, hotelSectionFieldMeta, { setValue }] = useField<HotelSectionFormValues | null>({
		name: `hotelSections.${stayConfiguration.hotelStayTypeId}`,
		validate: getStayConfigurationValidation(stayConfiguration) as any,
	});
	const hotelStays = useMemo(() => hotelSectionField.value?.hotelStays || [], [hotelSectionField.value?.hotelStays]);
	const maxStayPeriods = stayConfiguration.maxStayPeriods || Infinity;
	const multipleStaysAllowed = !(stayConfiguration.minStayPeriods === 1 && stayConfiguration.maxStayPeriods === 1);

	useEffect(() => {
		if (stayConfiguration.minStayPeriods > hotelStays.length) {
			const missingHotelStays = Array.from({ length: stayConfiguration.minStayPeriods - hotelStays.length }).map(() =>
				createEmptyHotelStay(stayConfiguration),
			);
			void setValue({ hotelStays: [...hotelStays, ...missingHotelStays] });
		}
	}, [hotelStays, setValue, stayConfiguration]);

	return (
		<FieldArray name={`hotelSections.${stayConfiguration.hotelStayTypeId}.hotelStays`}>
			{(helpers) => (
				<Container>
					<SectionHeader>
						<RegistrationHotelSectionTitle
							stayCount={hotelStays.length}
							maxStays={stayConfiguration.maxStayPeriods}
							error={
								(hotelSectionFieldMeta.touched || submitCount > 0) &&
								(hotelSectionFieldMeta.error as HotelSectionValidationResult)?.count
							}
						/>
						{multipleStaysAllowed && (
							<Button
								type="button"
								color="primary"
								onClick={() => helpers.push(createEmptyHotelStay(stayConfiguration))}
								prefix={<Icon $icon="plus" />}
								disabled={hotelStays.length >= maxStayPeriods}
								title={
									hotelStays.length >= maxStayPeriods
										? t("You can only assign a maximum of {{count}} hotel stays.", { count: maxStayPeriods })
										: undefined
								}
							>
								{stayConfiguration.wayOfBooking === WayOfBooking.BOOKING ? t("Add booking") : t("Add inquiry")}
							</Button>
						)}
					</SectionHeader>
					{hotelStays.length === 0 && <Empty>{t("No hotel stay added yet.")}</Empty>}
					<HotelStayList>
						{hotelStays.map((_, index) => {
							return (
								<HotelStay
									key={index}
									name={`hotelSections.${stayConfiguration.hotelStayTypeId}.hotelStays.${index}`}
									otherStays={hotelStays.filter((_, idx) => idx !== index)}
									heading={getHotelStayHeading(stayConfiguration, index, t)}
									registrationFormId={registrationFormId}
									guestId={guestId}
									sectionId={sectionId}
									stayConfiguration={stayConfiguration}
									onDelete={() => {
										helpers.remove(index);
										void setFieldTouched(`hotelSections.${stayConfiguration.hotelStayTypeId}`);
									}}
									onChange={(changedStay) => {
										helpers.replace(index, changedStay);
										void setFieldTouched(`hotelSections.${stayConfiguration.hotelStayTypeId}`);
									}}
									isDeletable={hotelStays.length > stayConfiguration.minStayPeriods}
									multipleStaysAllowed={multipleStaysAllowed}
								/>
							);
						})}
					</HotelStayList>
				</Container>
			)}
		</FieldArray>
	);
};
